import Swiper from 'swiper';
import { Navigation, Pagination, FreeMode, Thumbs, Autoplay, Zoom, Grid, Mousewheel } from 'swiper/modules';
import safeJSONParse from '../utils/safeJsonParse';
import { sanitizeSwiperLoop } from '../utils/swiperLoop';
import { BREAKPOINTS, SWIPER } from '../utils/constants';

// Standart Swiper init
const initSwiper = (slider = null) => {
  const isTargetedInit = Boolean(slider);
  const swipers = slider ? [slider] : document.querySelectorAll('[data-swiper]');
  const thumbSwipers = [];

  if (!swipers.length) return;

  if (!isTargetedInit) {
    window.theme.swiperObjects = [];
  }

  swipers.forEach(swiper => {
    if (swiper.closest('[data-product-gallery]')) return;
    if (swiper.swiper) return;

    const slides = swiper.querySelectorAll(SWIPER.SLIDE_CLASS);
    let config = swiper.dataset.swiperConfig ? safeJSONParse(swiper.dataset.swiperConfig, false) : {};
    const swiperModules = [];
    !config ? console.error('Error in Swiper JSON') : null;
    if (slides.length && config) {
      // Swiper operates by loading modules, that's why we check if a module is referenced in the configuration. If it is, we add it to the modules array.
      if ('navigation' in config) {
        swiperModules.push(Navigation);
      }
      if ('pagination' in config) {
        swiperModules.push(Pagination);
      }
      if ('freeMode' in config) {
        swiperModules.push(FreeMode);
      }
      if ('autoplay' in config) {
        swiperModules.push(Autoplay);
      }
      if ('mousewheel' in config) {
        swiperModules.push(Mousewheel);
      }
      if ('thumbs' in config) {
        swiperModules.push(Thumbs);
        thumbSwipers.forEach(thumbSwiper => {
          config = { ...config, ...{ thumbs: { swiper: thumbSwiper } } };
        });
      }
      if ('zoom' in config) {
        swiperModules.push(Zoom);
      }

      // Adding modules to the config
      config = sanitizeSwiperLoop({ ...config, ...{ modules: swiperModules } }, slides.length);

      swiper.classList.add('swiper--active');
      const newSwiper = new Swiper(swiper, config);

      // Swiper measures the container at init. Inside a closed `<details>` (or before layout),
      // width can be 0 and slide widths become garbage → overflow. Re-measure when opened / after paint.
      const detailsEl = swiper.closest('details');
      if (detailsEl) {
        const refreshSwiperInDetails = () => {
          requestAnimationFrame(() => newSwiper.update());
        };
        detailsEl.addEventListener('toggle', () => {
          if (detailsEl.open) refreshSwiperInDetails();
        });
        if (detailsEl.open) refreshSwiperInDetails();
      }

      if (swiper.dataset.swiperThumbs) {
        thumbSwipers.push(newSwiper);
      }

      if (swiper.querySelector(SWIPER.VIDEO_SLIDE)) {
        newSwiper.on('slideChangeTransitionStart', () => {
          const videoSlides = swiper.querySelectorAll(SWIPER.VIDEO_SLIDE);
          videoSlides.forEach(slide => {
            const isActive = slide.classList.contains('swiper-slide-active');
            const video = slide.querySelector('video');
            !isActive ? video.pause() : video.play();
          });
        });
      }
      window.theme.swiperObjects.push(newSwiper);
    }
  });
};

// Init swiper for mobile and tablet only
const initSwiperMobile = (slider = null) => {
  const desktopMediaQuery = window.matchMedia(`(min-width: ${BREAKPOINTS.DESKTOP}px)`);
  const swipers = slider ? [slider] : document.querySelectorAll('[data-swiper-mob]');

  if (!swipers.length) return;

  window.theme.swiperMobileObjects = [];

  const initSwiperOnMobile = () => {
    if (!desktopMediaQuery.matches) {
      swipers.forEach(swiper => {
        const slides = swiper.querySelectorAll(SWIPER.SLIDE_CLASS);
        let config = swiper.dataset.swiperConfig ? safeJSONParse(swiper.dataset.swiperConfig, false) : {};
        const swiperModules = [];
        !config ? console.error('Error in Swiper JSON') : null;
        if (slides.length && config) {
          if ('navigation' in config) {
            swiperModules.push(Navigation);
          }
          if ('pagination' in config) {
            swiperModules.push(Pagination);
          }
          if ('freeMode' in config) {
            swiperModules.push(FreeMode);
          }
          if ('autoplay' in config) {
            swiperModules.push(Autoplay);
          }
          if ('mousewheel' in config) {
            swiperModules.push(Mousewheel);
          }
          if ('thumbs' in config) {
            swiperModules.push(Thumbs);
          }
          if ('zoom' in config) {
            swiperModules.push(Zoom);
          }
          if ('grid' in config) {
            swiperModules.push(Grid);
          }

          config = sanitizeSwiperLoop({ ...config, ...{ modules: swiperModules } }, slides.length);
          swiper.classList.add('swiper--mob-active');
          const newSwiper = new Swiper(swiper, config);
          window.theme.swiperMobileObjects.push(newSwiper);

          if (swiper.querySelector(SWIPER.VIDEO_SLIDE)) {
            newSwiper.on('slideChangeTransitionStart', () => {
              const videoSlides = swiper.querySelectorAll(SWIPER.VIDEO_SLIDE);
              videoSlides.forEach(slide => {
                const isActive = slide.classList.contains('swiper-slide-active');
                const video = slide.querySelector('video');
                !isActive ? video.pause() : video.play();
              });
            });
          }
        }
      });
    } else {
      if (window.theme.swiperMobileObjects) {
        window.theme.swiperMobileObjects.forEach(swiper => {
          swiper.destroy();
        });
        const keys = Object.keys(window.theme.swiperMobileObjects);
        keys.forEach(key => window.theme.swiperMobileObjects[key].destroy());
        window.theme.swiperMobileObjects = [];
      }
    }
  };
  if (desktopMediaQuery) {
    initSwiperOnMobile();
  }
  desktopMediaQuery.addEventListener('change', initSwiperOnMobile);
};

export { initSwiper, initSwiperMobile };
